export type PipelineStageOption = {
  id: string
  code: string
  label: string
  sortOrder: number
  outcome: "in_pipeline" | "closed_won" | "closed_lost"
}

export type PipelineStageOptionInput = {
  id?: string | null
  code?: string | null
  label?: string | null
  sortOrder?: number | null
  outcome?: string | null
}

const FALLBACK_OPTIONS: PipelineStageOption[] = [
  { id: "", code: "qualification", label: "Qualification", sortOrder: 0, outcome: "in_pipeline" },
  { id: "", code: "discovery", label: "Discovery", sortOrder: 1, outcome: "in_pipeline" },
  { id: "", code: "solution_demo", label: "Solution & demo", sortOrder: 2, outcome: "in_pipeline" },
  { id: "", code: "proposal", label: "Proposal", sortOrder: 3, outcome: "in_pipeline" },
  { id: "", code: "negotiation", label: "Negotiation", sortOrder: 4, outcome: "in_pipeline" },
  { id: "", code: "closed_won", label: "Closed Won", sortOrder: 5, outcome: "closed_won" },
  { id: "", code: "closed_lost", label: "Closed Lost", sortOrder: 6, outcome: "closed_lost" },
]

export function normalizePipelineStageOptions(
  raw: PipelineStageOptionInput[] | null | undefined
): PipelineStageOption[] {
  if (!raw?.length) return [...FALLBACK_OPTIONS]
  const out: PipelineStageOption[] = []
  for (const row of raw) {
    const code = String(row.code ?? "").trim()
    const label = String(row.label ?? "").trim()
    if (!code && !label) continue
    const outcomeRaw = String(row.outcome ?? "in_pipeline").trim()
    const outcome =
      outcomeRaw === "closed_won" || outcomeRaw === "closed_lost"
        ? outcomeRaw
        : "in_pipeline"
    out.push({
      id: String(row.id ?? "").trim(),
      code: code || label.toLowerCase().replace(/\s+/g, "_"),
      label: label || code,
      sortOrder: typeof row.sortOrder === "number" ? row.sortOrder : out.length,
      outcome,
    })
  }
  return out.length ? out.sort((a, b) => a.sortOrder - b.sortOrder) : [...FALLBACK_OPTIONS]
}

export function pipelineStageOptionsWithNone(
  options: PipelineStageOption[]
): { value: string; label: string }[] {
  return [{ value: "", label: "None" }, ...options.map((o) => ({ value: o.code, label: o.label }))]
}

export function pipelineStageLabel(
  value: string | null | undefined,
  options?: PipelineStageOption[] | null
): string {
  const v = (value ?? "").trim()
  if (!v) return "None"
  const list = options?.length ? options : FALLBACK_OPTIONS
  const byCode = list.find((o) => o.code === v)
  if (byCode) return byCode.label
  const byId = list.find((o) => o.id === v)
  if (byId) return byId.label
  return v
}

export function pipelineStagePosition(
  value: string | null | undefined,
  options?: PipelineStageOption[] | null
): { current: number; total: number } | null {
  const v = (value ?? "").trim()
  if (!v) return null
  const list = (options?.length ? options : FALLBACK_OPTIONS).filter(
    (o) => o.outcome === "in_pipeline"
  )
  const idx = list.findIndex((option) => option.code === v || option.id === v)
  if (idx < 0) return null
  return { current: idx + 1, total: list.length }
}

/** @deprecated Use options from contact/pipeline BFF payloads. */
export const PIPELINE_STAGE_OPTIONS = pipelineStageOptionsWithNone(FALLBACK_OPTIONS)

/** @deprecated Use options from contact/pipeline BFF payloads. */
export const PIPELINE_STAGES = FALLBACK_OPTIONS.filter((o) => o.code)
